import { differenceInDays } from 'date-fns';
import { TpaMonthsMatrix } from './useTPA';

export const getUpcomingTpaPayment = (tpaMatrix: TpaMonthsMatrix[]) => {
  const upcomingTpaPayment: TpaMonthsMatrix | undefined = tpaMatrix.find(
    tpaPaymentItem => {
      const currentDate = new Date();
      return (
        differenceInDays(tpaPaymentItem.paymentDate, currentDate) >= 0 &&
        tpaPaymentItem.doesBillExist &&
        !tpaPaymentItem.isPaymentCompleted
      );
    },
  );
  return upcomingTpaPayment;
};

export const getTpaPaymentsOverDue = (tpaMatrix: TpaMonthsMatrix[]) => {
  const overDueTpaPayment: TpaMonthsMatrix[] = tpaMatrix.filter(
    tpaPaymentItem => {
      const currentDate = new Date();
      return (
        differenceInDays(tpaPaymentItem.paymentDate, currentDate) < 0 &&
        tpaPaymentItem.doesBillExist &&
        !tpaPaymentItem.isPaymentCompleted
      );
    },
  );
  return overDueTpaPayment;
};

export const getAllTpaPaymentsOverDue = (tpaMatrix: TpaMonthsMatrix[]) => {
  const currentDate = new Date();
  const endOfCurrentMonth = new Date(
    currentDate.getFullYear(),
    currentDate.getMonth() + 1,
    0,
    23,
    59,
    59,
    999,
  );

  return tpaMatrix.filter(tpaPaymentItem => {
    const paymentDate = new Date(tpaPaymentItem.paymentDate);
    return (
      paymentDate <= endOfCurrentMonth && !tpaPaymentItem.isPaymentCompleted
    );
  });
};

export const getTpaPaymentsOverDueYearly = (tpaMatrix: TpaMonthsMatrix[]) => {
  const currentDate = new Date();
  currentDate.setHours(0, 0, 0, 0); // Strip time for accurate date comparison
  const currentMonth = currentDate.getMonth();
  const currentYear = currentDate.getFullYear();

  // Step 1: Filter overdue (isPaymentCompleted === false and in the current or earlier month)
  const overdueItems = tpaMatrix.filter(item => {
    const paymentDate = new Date(item.paymentDate);
    paymentDate.setHours(0, 0, 0, 0);
    const paymentMonth = paymentDate.getMonth();
    const paymentYear = paymentDate.getFullYear();

    return paymentDate <= currentDate && !item.isPaymentCompleted;
  });

  // Step 2: Check if we need to include next month
  const result = [...overdueItems];

  const lastOverdue = overdueItems[overdueItems.length - 1];

  if (lastOverdue) {
    const lastPaymentDate = new Date(lastOverdue.paymentDate);
    lastPaymentDate.setHours(0, 0, 0, 0);

    if (lastPaymentDate < currentDate) {
      // Find the next month's object
      const nextMonthItem = tpaMatrix.find(
        item => item.monthNumber === lastOverdue.monthNumber + 1,
      );
      if (nextMonthItem && !result.includes(nextMonthItem)) {
        result.push(nextMonthItem);
      }
    }
  }

  return result;
};

export const getLastIsPaymentCompleted = (data: TpaMonthsMatrix[]) => {
  const lastIsPaymentCompleted = data?.filter(
    (entry: any) => entry.isPaymentCompleted,
  );
  return lastIsPaymentCompleted;
};

export const getUnpaidMonthFormLastIsPaymentCompleted = (
  data: TpaMonthsMatrix[],
) => {
  const lastCompletedIndex = [...data]
    .reverse()
    .findIndex(item => item.isPaymentCompleted);
  const cutIndex =
    lastCompletedIndex !== -1 ? data.length - lastCompletedIndex : data.length;
  const result = data.slice(cutIndex);
  return result
    ? getAllTpaPaymentsOverDue(result).filter(item => !item.isPaymentCompleted)
    : [];
};

export const getTPAYears = (tpaMatrix: TpaMonthsMatrix[]) => {
  const tpaYearsArray: string[] = [
    ...new Set(
      tpaMatrix.map(({ paymentDate }) =>
        new Date(paymentDate).getFullYear().toString(),
      ),
    ),
  ];
  return tpaYearsArray;
};

export const getSelectedYearTPAMonthMatrix = (
  year: string,
  tpaMatrix: TpaMonthsMatrix[],
) => {
  const currentYearMatrix = tpaMatrix.filter(item => {
    return year === item.paymentDate.getFullYear().toString() && item;
  });
  return currentYearMatrix;
};
